import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Check, 
  Crown, 
  Briefcase, 
  GraduationCap, 
  ArrowLeft,
  Zap,
  Eye,
  Star,
  Users,
  BadgeCheck,
  X
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ConnectPlansPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [plansConfig, setPlansConfig] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlansConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/hotmart/plans-config`);
      setPlansConfig(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar configuração de planos:', error);
      }
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/connect/profiles/user/${user.id}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar perfil do usuário:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlansConfig();
    if (isAuthenticated && user?.id) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchPlansConfig, fetchUserProfile]);

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      subtitle: 'Para Todos',
      icon: Users,
      color: 'from-gray-500 to-gray-600',
      borderColor: 'border-gray-600',
      bgColor: 'bg-gray-600',
      description: 'Acesso básico à rede BIM Connect',
      yearlyPrice: 0,
      free: true,
      features: [
        { text: 'Criar perfil completo', included: true },
        { text: 'Adicionar certificações', included: true },
        { text: 'Conectar com outros', included: true },
        { text: 'Publicar no feed', included: true },
        { text: 'Pesquisar profissionais', included: true },
        { text: 'Acesso básico ao BIM Map', included: true },
        { text: 'Candidatar a vagas (Em breve)', included: false },
        { text: 'Acesso ao Fórum', included: false },
      ],
      hotmartKey: 'free'
    },
    {
      id: 'basic',
      name: 'Básico',
      subtitle: 'Comece a Conectar',
      icon: GraduationCap,
      color: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-500',
      bgColor: 'bg-blue-500',
      description: 'Tudo do plano Gratuito + acesso aos contatos do AJ | BIM Map',
      yearlyPrice: 79.90,
      priceLabel: '79,90',
      installments: 'ou 12x',
      features: [
        { text: 'Tudo do plano Gratuito', included: true },
        { text: 'Acesso ao AJ | BIM Map', included: true },
        { text: 'Ver contatos de membros e empresas do BIM Map', included: true, highlight: true },
        { text: 'Acesso ao Fórum de dúvidas BIM', included: true },
        { text: 'Candidatar a vagas (Em breve)', included: false },
        { text: 'Perfil em destaque', included: false },
      ],
      hotmartKey: 'basic'
    },
    {
      id: 'plus',
      name: 'Plus',
      subtitle: 'Para Profissionais',
      icon: Briefcase,
      color: 'from-orange-500 to-orange-600',
      borderColor: 'border-orange-500',
      bgColor: 'bg-orange-500',
      description: 'Para profissionais BIM que querem se destacar',
      yearlyPrice: 89.90,
      priceLabel: '89,90',
      installments: 'ou 12x',
      comingSoon: true,
      features: [
        { text: 'Tudo do plano Básico', included: true },
        { text: 'Perfil em destaque nas buscas', included: true },
        { text: 'Badge profissional', included: true },
        { text: 'Notícias BIM atualizadas', included: true },
        { text: 'Acesso às Bibliotecas BIM', included: true },
        { text: 'Publicar vagas', included: false },
      ],
      hotmartKey: 'plus'
    }
  ];

  const handleSelectPlan = (plan) => {
    if (!plansConfig) return;
    const planConfig = plansConfig.plans[plan.hotmartKey];
    if (!planConfig) return;
    window.open(planConfig.yearly.checkout_url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <button
            onClick={() => navigate('/connect')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
            data-testid="back-to-connect"
          >
            <ArrowLeft size={20} />
            Voltar ao BIM Connect
          </button>

          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
            <Crown className="text-orange-500" size={18} />
            <span className="text-orange-400 text-sm font-medium">Planos BIM Connect</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Escolha seu <span className="text-orange-500">plano</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Desbloqueie todo o potencial da maior rede profissional BIM da América Latina
          </p>

          <div className="inline-flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-full px-5 py-2 mb-12">
            <BadgeCheck className="text-green-400" size={16} />
            <span className="text-gray-300 text-sm">Planos anuais · pagamento único por ano</span>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const isCurrentPlan = userProfile?.plan === plan.id;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-gray-800/50 backdrop-blur-sm rounded-2xl border-2 ${
                    plan.popular ? 'border-orange-500' : 'border-gray-700'
                  } overflow-hidden transition-all hover:scale-[1.02] ${plan.comingSoon ? 'opacity-70' : ''}`}
                  data-testid={`plan-${plan.id}`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white text-center py-2 text-sm font-medium">
                      <Star className="inline-block mr-1" size={14} />
                      Mais Popular
                    </div>
                  )}

                  {/* Coming Soon Badge */}
                  {plan.comingSoon && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-zinc-600 to-zinc-700 text-white text-center py-2 text-sm font-medium">
                      Em breve
                    </div>
                  )}

                  <div className={`p-6 ${(plan.popular || plan.comingSoon) ? 'pt-14' : ''}`}>
                    {/* Plan Header */}
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${plan.color} flex items-center justify-center mb-4`}>
                      <Icon className="text-white" size={28} />
                    </div>

                    <div className="mb-2">
                      <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                      <span className={`text-sm font-medium ${
                        plan.popular ? 'text-orange-400' :
                        plan.id === 'premium' ? 'text-purple-400' : 'text-blue-400'
                      }`}>
                        {plan.subtitle}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-6 min-h-[40px]">{plan.description}</p>

                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-gray-400">R$</span>
                        <span className="text-4xl font-bold text-white">{plan.priceLabel || plan.yearlyPrice}</span>
                        {plan.yearlyPrice > 0 && <span className="text-gray-400">/ano</span>}
                      </div>
                      {plan.yearlyPrice > 0 && (
                        <p className="text-gray-500 text-xs mt-1">{plan.installments ? `${plan.installments} na Hotmart · cobrança anual` : 'Cobrança anual única'}</p>
                      )}
                    </div>

                    {/* CTA Button */}
                    {plan.comingSoon ? (
                      <button
                        disabled
                        className="w-full py-3 rounded-xl bg-zinc-700 text-gray-400 font-medium cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid={`select-plan-${plan.id}`}
                      >
                        Em breve
                      </button>
                    ) : isCurrentPlan ? (
                      <button
                        disabled
                        className="w-full py-3 rounded-xl bg-gray-700 text-gray-400 font-medium cursor-not-allowed"
                      >
                        <BadgeCheck className="inline-block mr-2" size={18} />
                        Plano Atual
                      </button>
                    ) : plan.free ? (
                      <button
                        onClick={() => navigate('/connect')}
                        className="w-full py-3 rounded-xl bg-gray-600 hover:bg-gray-500 text-white font-medium transition-opacity flex items-center justify-center gap-2"
                        data-testid="select-plan-free"
                      >
                        <Users size={18} />
                        Criar Conta
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full py-3 rounded-xl bg-gradient-to-r ${plan.color} text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                        data-testid={`select-plan-${plan.id}`}
                      >
                        <Zap size={18} />
                        Assinar Agora
                      </button>
                    )}

                    {/* Features */}
                    <div className="mt-6 space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {plan.features.map((feature, index) => (
                        <div key={feature.text} className="flex items-start gap-2">
                          {feature.included ? (
                            <Check className={`flex-shrink-0 mt-0.5 ${
                              feature.highlight ? 'text-purple-400' : 'text-green-500'
                            }`} size={16} />
                          ) : (
                            <X className="text-gray-600 flex-shrink-0 mt-0.5" size={16} />
                          )}
                          <span className={`text-xs ${
                            feature.included
                              ? feature.highlight
                                ? 'text-purple-300 font-medium'
                                : 'text-gray-300'
                              : 'text-gray-500'
                          }`}>
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-16 px-4 border-t border-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-12">
            Por que assinar o BIM Connect?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                <Users className="text-orange-500" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Networking</h3>
              <p className="text-gray-400 text-sm">
                Conecte-se com milhares de profissionais BIM e expanda sua rede de contatos
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="text-blue-500" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Oportunidades</h3>
              <p className="text-gray-400 text-sm">
                Acesse vagas exclusivas e seja encontrado por empresas que buscam talentos BIM
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Eye className="text-purple-500" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Visibilidade</h3>
              <p className="text-gray-400 text-sm">
                Destaque seu perfil e seja visto por recrutadores e empresas do setor
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Trust Section */}
      <section className="py-16 px-4 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Pagamento Seguro</h2>
          <p className="text-gray-400 mb-8">
            Todos os pagamentos são processados de forma segura pela Hotmart, 
            a maior plataforma de produtos digitais da América Latina.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500">
            <span className="flex items-center gap-1">🔒 Pagamento Seguro</span>
            <span className="hidden md:inline">•</span>
            <span className="flex items-center gap-1">💳 Cartão ou PIX</span>
            <span className="hidden md:inline">•</span>
            <span className="flex items-center gap-1">↩️ 7 dias de garantia</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConnectPlansPage;
